<template>
  <div class="ParseCloud">

     <div v-if="!cloudActive" class="ParseCloud__ignite_folder mb-4">
      <span>
        You are using Ignite folder and can't change cloud code via editor
      </span>
    </div>

    <div class="mb-4 d-flex align-center justify-space-between">
      <!-- Tab-style buttons -->
      <div class="tab-container">
        <button 
          @click="loadParse()" 
          :class="['tab-button', { 'tab-button--active': activatedTab == 0 }]"
        >
          Cloud Code
        </button>
        <button 
          @click="loadParse(true)" 
          :class="['tab-button', { 'tab-button--active': activatedTab == 1 }]"
        >
          Templates
        </button>
        <button 
          @click="loadExpress()" 
          :class="['tab-button', { 'tab-button--active': activatedTab == 2 }]"
        >
          Express Cloud
        </button>
      </div>
      
      <!-- Logs button -->
      <v-tooltip bottom v-if="cloudEnabled">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            @click="showServiceLogs"
            :disabled="btnDisabled"
            v-bind="attrs"
            v-on="on"
            color="orange"
            class="ml-2"
          >
            <icon-console />
          </v-btn>
        </template>
        <span>View Logs</span>
      </v-tooltip>
    </div>

    <codemirror
      id="codemirror_text_area"
      v-model="customCode"
      :options="codeOptions"
      v-if="activatedTab == 0"
      ref="myCm"
      @ready="onCmReady"
    />

    <codemirror
      id="codemirror_text_area"
      v-model="templatesJson"
      :options="templatesOptions"
      v-else-if="activatedTab == 1"
      ref="myCm"
      @ready="onCmReady"
    />

    <codemirror
      id="codemirror_text_area"
      v-model="expressCode"
      :options="codeOptions"
      v-else-if="activatedTab == 2"
      ref="myCm"
      @ready="onCmReady"
    />

    <div class="mt-4" align="right" v-if="changed && !saving && cloudActive">
      <v-btn @click="resetChanges" class="mr-2" dark color="grey" medium>
        Reset
      </v-btn>
      <v-btn @click="save" dark color="orange" medium> Save </v-btn>
    </div>

    <div class="mt-8" v-if="versions.length == 0 && cloudActive" align="center">
      <h3>Create your first version to see the version history</h3>
    </div>
    <div class="mt-8" v-else>
      <SiteVersionElement
        v-for="version in versions"
        :arrayType="activatedTab == 2 ? 2 : 1"
        :key="version.id"
        :version="version"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import SiteVersionElement from '@/components/TheSite/SiteVersion/SiteVersionElement'
import CodeMirror, { codemirror } from 'vue-codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/theme/base16-dark.css'
import { MODAL_FORGE_CONSOLE } from '@/components/Modals'
import IconConsole from '@/components/Icons/IconConsole.vue'

export default {
  name: 'SiteAppParseCloud',
  components: { codemirror, SiteVersionElement, IconConsole },
  data() {
    return {
      defaultTemplates: null,
      defaultCode: null,
      defaultExpressCode: null,
      id: null,
      expressId: null,
      parse_setting_id: null,
      current_version_id: null,
      customCode: '',
      expressCode: '',
      codemirrorObject: null,
      saving: false,
      templatesJson: '',
      activatedTab: 0,
      codeOptions: {
        tabSize: 2,
        mode: 'text/javascript',
        lint: true,
        theme: 'base16-dark',
        lineNumbers: true,
        line: true,
      },
      templatesOptions: {
        tabSize: 2,
        mode: {
          name: 'javascript',
          json: true,
        },
        lint: true,
        theme: 'base16-dark',
        lineNumbers: true,
        line: true,
      },
    }
  },
  mounted() {
    this.$store.dispatch('parse_cloud/load', this.$route.params.siteAppId)
  },
  computed: {
    changed() {
      let changedCode = false
      if (this.activatedTab == 0)
        changedCode = this.customCode != this.defaultCode
      else if (this.activatedTab == 1)
        changedCode = this.templatesJson != this.defaultTemplates
      else if (this.activatedTab == 2)
        changedCode = this.expressCode != this.defaultExpressCode;
      return changedCode;
    },
    parseServerApiUrl() {
      let parse_url = this.$store.state.site_app.parseSetting?.parse_server_app_url;
      return parse_url.replace('/parse', '/cloud_api');
    },
    versions() {
      if (this.id == null) return []
      let versions = []
      if (this.activatedTab == 2) {
        versions = this.$store.state.versions.list
          .filter((v) => v.entity_type =='ExpressCode' && v.entity_id == this.expressId)
          .sort((a, b) => b.scopedId - a.scopedId)
      }
      else
        versions = this.$store.state.versions.list
          .filter((v) => v.entity_type == 'ParseCustomCode' && v.entity_id == this.id)
          .sort((a, b) => b.scopedId - a.scopedId)
      return versions
    },
    cloudActive() {
      return !this.activeSite.ignite_folder_added
    },
    cloudEnabled() {
      if (this.$store.state.site_app.parseSetting != null) {
        return this.$store.state.site_app.parseSetting.cloud_code_enabled
      } else return false
    },
    btnDisabled() {
      return (
        this.saving || this.$store.state.site_app.parseSetting?.deploying || this.$store.state.site_app.parseSetting?.redeploy
      )
    },
  },
  methods: {
    getExpressCloudHelpText(url) {
      return `
// Express Cloud Quick Start
// The following is available by default:
// const router = express.Router()
//
// To add your endpoints, use the 'router' variable. Example:
//
// router.get('/forge', (req, res) => {
//   res.send('The forge is hot and ready')
// })
//
// This API will be available at path:
// ${url}/about
`;
    },
    getTemplatesHelpText() {
      return `[
  // Chisel CMS Site Templates
  // 
  // Templates are used to define custom content models and fields for your Chisel CMS site.
  // Each template contains one or more models, and each model contains fields.
  //
  // Template Structure:
  // - name: Display name for the template
  // - description: Description of what the template is for
  // - icon: Icon file name (e.g., "blog.svg", "gallery.svg")
  // - models: Array of content models
  //
  // Model Structure:
  // - name: Display name for the model
  // - nameId: Unique identifier for the model
  // - description: Description of the model
  // - color: Color for the model (rgba format)
  // - fields: Array of fields for the model
  //
  // Field Structure:
  // - name: Display name for the field
  // - nameId: Unique identifier for the field
  // - type: Field type (Short Text, Long Text, Media, Reference, Date/time, etc.)
  // - appearance: How the field appears (Single line, Markdown, Media, Reference, etc.)
  // - color: Color for the field (rgba format)
  // - isRequired: Whether the field is required
  // - isTitle: Whether this field is the title field
  // - isList: Whether this field can have multiple values
  // - order: Display order of the field
  // - validations: Validation rules for the field
  //
  // Example Templates:
  {
    "name": "Blog",
    "description": "The template for blog.",
    "icon": "blog.svg",
    "models": [
      {
        "name": "Author",
        "nameId": "Author",
        "description": "",
        "color": "rgba(187, 34, 230, 1)",
        "fields": [
          {
            "name": "Name",
            "nameId": "Name",
            "type": "Short Text",
            "appearance": "Single line",
            "color": "rgba(16, 178, 99, 1)",
            "boolTextYes": "",
            "boolTextNo": "",
            "validValues": [],
            "isRequired": true,
            "isTitle": true,
            "isList": false,
            "isDisabled": false,
            "order": 0,
            "validations": null
          },
          {
            "name": "Profile Photo",
            "nameId": "Profile_Photo",
            "type": "Media",
            "appearance": "Media",
            "color": "rgba(3, 22, 230, 1)",
            "boolTextYes": "",
            "boolTextNo": "",
            "validValues": [],
            "isRequired": false,
            "isTitle": false,
            "isList": false,
            "isDisabled": false,
            "order": 1,
            "validations": {
              "fileTypes": {
                "active": true,
                "types": ["Image"],
                "errorMsg": "",
                "isError": false
              }
            }
          },
          {
            "name": "Biography",
            "nameId": "Biography",
            "type": "Long Text",
            "appearance": "Markdown",
            "color": "rgba(245, 200, 30, 1)",
            "boolTextYes": "",
            "boolTextNo": "",
            "validValues": [],
            "isRequired": false,
            "isTitle": false,
            "isList": false,
            "isDisabled": false,
            "order": 2,
            "validations": null
          }
        ]
      },
      {
        "name": "Post",
        "nameId": "Post",
        "description": "",
        "color": "rgba(210, 230, 8, 1)",
        "fields": [
          {
            "name": "Title",
            "nameId": "Title",
            "type": "Short Text",
            "appearance": "Single line",
            "color": "rgba(255, 32, 89, 1)",
            "boolTextYes": "",
            "boolTextNo": "",
            "validValues": [],
            "isRequired": true,
            "isTitle": true,
            "isList": false,
            "isDisabled": false,
            "order": 0,
            "validations": null
          },
          {
            "name": "Slug",
            "nameId": "Slug",
            "type": "Short Text",
            "appearance": "Slug",
            "color": "rgba(76, 144, 8, 1)",
            "boolTextYes": "",
            "boolTextNo": "",
            "validValues": [],
            "isRequired": true,
            "isTitle": false,
            "isList": false,
            "isDisabled": false,
            "order": 1,
            "validations": null
          },
          {
            "name": "Author",
            "nameId": "Author",
            "type": "Reference",
            "appearance": "Reference",
            "color": "rgba(1, 34, 54, 1)",
            "boolTextYes": "",
            "boolTextNo": "",
            "validValues": [],
            "isRequired": false,
            "isTitle": false,
            "isList": false,
            "isDisabled": false,
            "order": 2,
            "validations": {
              "models": {
                "active": true,
                "modelsList": ["Author"],
                "errorMsg": "",
                "isError": false
              }
            }
          },
          {
            "name": "Body",
            "nameId": "Body",
            "type": "Long Text",
            "appearance": "Markdown",
            "color": "rgba(162, 174, 249, 1)",
            "boolTextYes": "",
            "boolTextNo": "",
            "validValues": [],
            "isRequired": true,
            "isTitle": false,
            "isList": false,
            "isDisabled": false,
            "order": 3,
            "validations": null
          }
        ]
      }
    ]
  }
  // Add more templates here...
]`;
    },
    onCmReady(cm) {
      this.codemirrorObject = cm
    },
    changeCmReadOnly() {
      if (this.codemirrorObject == null)
        return
      this.codemirrorObject.setOption('readOnly', !this.cloudActive)
    },
    loadParse(isTemplates = false) {
      this.activatedTab = isTemplates ? 1 : 0
      this.$store.dispatch('parse_cloud/load', this.$route.params.siteAppId)
    },
    loadExpress() {
      this.activatedTab = 2
      this.$store.dispatch('express_cloud/load', this.$route.params.siteAppId)
    },
    saveParse() {
      if (!this.cloudActive || this.saving) return

      this.saving = true
      const params = {
        id: this.id,
        parse_custom_code: {
          id: this.id,
          parse_setting_id: this.parse_setting_id,
          templates_json: this.templatesJson,
          custom_code: this.customCode,
        },
      }
      this.$store
        .dispatch('parse_cloud/update', params)
        .then(() => {
          this.saving = false
        })
        .catch((e) => {
          this.saving = false
          console.log(e)
        })
    },
    saveExpress() {
      if (this.saving) return

      this.saving = true
      const params = {
        id: this.expressId,
        express_code: {
          id: this.expressId,
          parse_setting_id: this.parse_setting_id,
          code_text: this.expressCode,
        },
      }
      this.$store
        .dispatch('express_cloud/update', params)
        .then(() => {
          this.saving = false
        })
        .catch((e) => {
          this.saving = false
          console.log(e)
        })
    },
    save() {
      this.activatedTab == 2 ? this.saveExpress() : this.saveParse()
    },
    resetChanges() {
      if (this.activatedTab == 0) {
        this.customCode = this.defaultCode
      } else if (this.activatedTab == 1) {
        this.templatesJson = this.defaultTemplates
      } else if (this.activatedTab == 2) {
        this.expressCode = this.defaultExpressCode
      }
    },
    showServiceLogs() {
      if (this.$store.state.site_app.parseSetting) {
        this.$store.commit('site_app/setServiceMessages', {
          message: [],
          id: this.$store.state.site_app.parseSetting.appId,
        })
        this.$store.dispatch(
          'site_app/checkParseLogs',
          this.$store.state.site_app.parseSetting.site_app_id
        )
        this.$store.commit('application/openModal', {
          component: MODAL_FORGE_CONSOLE,
          props: {
            app: true,
            appType: 1,
          },
        })
      }
    },
  },
  watch: {
    '$store.state.parse_cloud.current': {
      handler(newVal, oldVal) {
        this.defaultTemplates = newVal.templates_json
        this.defaultCode = newVal.custom_code
        this.id = newVal.id
        this.parse_setting_id = newVal.parse_setting_id
        this.current_version_id = newVal.current_version_id
        if (this.customCode.length == 0) this.customCode = newVal.custom_code
        if (this.templatesJson.length == 0) {
          if (!newVal.templates_json || newVal.templates_json.trim() === '') {
            this.templatesJson = this.getTemplatesHelpText()
          } else {
            this.templatesJson = newVal.templates_json
          }
        }
      },
    },
    '$store.state.express_cloud.current': {
      handler(newVal, oldVal) {
        const helpText = this.getExpressCloudHelpText(this.parseServerApiUrl);
        if (!newVal.codeText || newVal.codeText.trim() === '') {
          this.defaultExpressCode = helpText;
          this.expressCode = helpText;
        } else {
          this.defaultExpressCode = newVal.codeText;
          if (this.expressCode.length == 0 ) this.expressCode = newVal.codeText;
        }
        this.expressId = newVal.id;
        this.parse_setting_id = newVal.parse_setting_id;
        this.current_version_id = newVal.current_version_id;
      }
    },
    '$store.state.sites.active.ignite_folder_added': {
      handler(newVal, oldVal) {
        if (newVal != oldVal) this.changeCmReadOnly()
      }
    }
  },
}
</script>

<style lang="scss">
.ParseCloud {
  padding: 42px 0px 24px;
  margin: auto;
  &__ignite_folder {
    text-align: center;
    padding: 40px;
    color: orange;
    background: #36344c;
    border-radius: 5px;
    margin-top: 33px;
    font-family: Loto;
    font-size: 16px;
  }
  .express-cloud {
    background: #181729;
    padding: 15px;
    border-radius: 4px;
    margin-top: 15px;
    .express-cloud-info {
      width: 400px;
      text-align: left;
      .code-block {
        background: #36344c;
        width:288px;
      }
      .orange-text {
        color: orange;
      }
    }
  }
}

.tab-container {
  display: flex;
  background: #2e2c42;
  border-radius: 8px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tab-button {
  background: transparent;
  border: none;
  color: #a4a4ae;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  text-align: center;
  font-family: 'Instrument Sans', sans-serif;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 126, 33, 0.3);
  }

  &--active {
    background: #ff7e21;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(255, 126, 33, 0.3);

    &:hover {
      background: #ff8a2e;
    }
  }
}

.CodeMirror {
  height: 900px;
  max-height: 80%;
}
</style>
